import React, { useEffect, useRef, useState } from "react";
import { NavLink, Outlet } from "react-router-dom";
import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import { useGSAP } from "@gsap/react";
import "./PortfolioWorks.css";
import ResponsiveWorks from "./ResponsiveWorks";
import { Icon } from "@iconify/react";
import { useTranslation } from "react-i18next";

gsap.registerPlugin(ScrollTrigger);

const PortfolioWorks = () => {
  const { t, i18n } = useTranslation();
  const [resize, setResize] = useState(window.innerWidth);
 
  useEffect(() => {
    const path = window.location.pathname.toLowerCase();
    const dropdownMap = {
      media: [
        "renting-studios",
        "post-production",
        "photography",
        "videography",
      ],
      education: [
        "tranning-platforms",
        "tranning-bags",
        "electronic-marketing",
        "tranning-content",
        "project-management",
        "virtual-project",
      ],
      web: ["web-design", "development"],
      marketing: [
        "social-media",
        "media-buying",
        "infleuncer-marketing",
        "seo-services",
      ],
      event: ["implementation", "conference"],
    };
  
    const lastSegment = path.split("/").filter(Boolean).pop();
    for (const [dropdownName, children] of Object.entries(dropdownMap)) {
      if (children.includes(lastSegment)) {
        setOpenDropdown(dropdownName);
        sectionRef.current.scrollIntoView();
        break;
      }
    }
  }, []);
  
  useEffect(() => {
    const resizeWidth = () => setResize(window.innerWidth);
    window.addEventListener("resize", resizeWidth);
    return () => window.removeEventListener("resize", resizeWidth);
  }, []);

  const [openDropdown, setOpenDropdown] = useState(null);
  const sectionRef = useRef(null);
  const dropdownsRef = useRef(null);
  const [filter, setFilter] = useState(false);
  const toggleDropdown = (name) => {
    setOpenDropdown((prev) => (prev === name ? null : name));
  };

  const closeAllDropdowns = () => {
    setOpenDropdown(null);
  };

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (
        dropdownsRef.current &&
        !dropdownsRef.current.contains(event.target)
      ) {
        closeAllDropdowns();
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  useGSAP(() => {
    const el = sectionRef.current;
    gsap.fromTo(
      el.querySelectorAll(".fade-in"),
      { opacity: 0, y: 50 },
      {
        opacity: 1,
        y: 0,
        duration: 1,
        stagger: 0.2,
        ease: "power2.out",
        scrollTrigger: {
          trigger: el,
          start: "top 80%",
          toggleActions: "play none none reverse",
        },
      }
    );
  });

  const pathSegments = window.location.pathname.split("/");
  const lastSegment = pathSegments.filter(Boolean).pop();
  const cleanName = lastSegment.replace(/-/g, " ");

  return (
    <div 
      id="#works"
      ref={sectionRef}
      className="z-50 PortfolioWorks py-[4vh] md:py-[10vh]"
    >
      {resize < 700 ? (
        <div className="flex items-center justify-between px-[3vw]">
          <p className="py-3 px-5 font-semibold text-white bg-[#cbb434] rounded min-w-[20vw] text-center capitalize">
            {t(cleanName)}
          </p>

          <div
            onClick={() => setFilter((prev) => !prev)}
            className="cursor-pointer flex items-center gap-4 px-5 py-2 rounded border border-[#d0d1d5]"
          >
            <Icon icon="tabler:filter-2" width="25" height="25" />
            <span>{t("Filter")}</span>
          </div>
        </div>
      ) : (
        <div
          className={`categories ${
            openDropdown ? "h-[140px]" : "h-[70px]"
          } !duration-500 md:px-[7vw] fade-in z-[999]`}
          ref={dropdownsRef}
        >
          <NavLink to="all" onClick={closeAllDropdowns}>
            {t("All")}
          </NavLink>

          <div className="flex flex-col items-center relative z-50">
            <button
              className={`${openDropdown === "media" && "active"}`}
              onClick={() => toggleDropdown("media")}
            >
              {t("Media Production")}
            </button>
            <div
              className={`list flex overflow-hidden mt-4 duration-500 ${
                openDropdown === "media" ? "open" : "h-0"
              } absolute top-[100%] ${i18n.language === 'ar' ? "" :"left-0"} z-[9999]`}
            >
              <NavLink
                to="renting-studios"
                className="child"
                onClick={closeAllDropdowns}
              >
                {t("Renting Company Studios")}
              </NavLink>
              <NavLink
                to="post-production"
                className="child"
                onClick={closeAllDropdowns}
              >
                {t("Post Production")}
              </NavLink>
              <NavLink
                className="child"
                to="Photography"
                onClick={closeAllDropdowns}
              >
                {t("Photography")}
              </NavLink>
              <NavLink
                className="child"
                to="videography"
                onClick={closeAllDropdowns}
              >
                {t("Videography")}
              </NavLink>
            </div>
          </div>

          <div className="flex flex-col items-center relative z-50">
            <button
              className={`${openDropdown === "education" && "active"}`}
              onClick={() => toggleDropdown("education")}
            >
              {t("Education Services")}
            </button>
            <div
              className={`list education z-50 flex ${
                resize > 768  ? "flex-row left-[-190%]" : "flex-col left-0"
              }  ${  resize > 768  & i18n.language === 'ar' && '!left-[-350%]'}  overflow-hidden mt-4 duration-500 ${
                openDropdown === "education" ? "h-[450px] md:h-[70px]" : "h-0"
              } absolute top-[100%] z-50`}
            >
              <NavLink
                to="tranning-platforms"
                className="child"
                onClick={closeAllDropdowns}
              >
                {t("Management of training platforms")}
              </NavLink>
              <NavLink
                to="tranning-bags"
                className="child"
                onClick={closeAllDropdowns}
              >
                {t("Preparing training bags")}
              </NavLink>
              <NavLink
                className="child"
                to="electronic-marketing"
                onClick={closeAllDropdowns}
              >
                {t("Electronic marketing")}
              </NavLink>
              <NavLink
                className="child"
                to="tranning-content"
                onClick={closeAllDropdowns}
              >
                {t("Register training content")}
              </NavLink>
              <NavLink
                className="child"
                to="project-management"
                onClick={closeAllDropdowns}
              >
                {t("Project management")}
              </NavLink>
              <NavLink
                className="child"
                to="virtual-project"
                onClick={closeAllDropdowns}
              >
                {t("Virtual projects")}
              </NavLink>
            </div>
          </div>

          <div className="flex flex-col items-center relative">
            <button
              className={`${openDropdown === "web" && "active"}`}
              onClick={() => toggleDropdown("web")}
            >
              {t("Web Solution")}
            </button>
            <div
              className={`list flex overflow-hidden mt-4 duration-500 ${
                openDropdown === "web" ? "h-14" : "h-0"
              } absolute top-[100%] left-0 z-50`}
            >
              <NavLink
                to="web-design"
                className="child"
                onClick={closeAllDropdowns}
              >
                {t("Web Design")}
              </NavLink>
              <NavLink
                to="development"
                className="child"
                onClick={closeAllDropdowns}
              >
                {t("Web Development")}
              </NavLink>
            </div>
          </div>

          <div className="flex flex-col items-center relative">
            <button
              className={`${openDropdown === "marketing" && "active"}`}
              onClick={() => toggleDropdown("marketing")}
            >
              {t("Digital Marketing")}
            </button>
            <div
              className={`list flex overflow-hidden mt-4 duration-500 ${
                openDropdown === "marketing" ? "h-14" : "h-0"
              } absolute top-[100%] z-50`}
            >
              <NavLink
                className="child"
                to="social-media"
                onClick={closeAllDropdowns}
              >
                {t("Social media")}
              </NavLink>
              <NavLink
                className="child"
                to="media-buying"
                onClick={closeAllDropdowns}
              >
                {t("Media buying")}
              </NavLink>
              <NavLink
                className="child"
                to="infleuncer-marketing"
                onClick={closeAllDropdowns}
              >
                {t("Influencer Marketing")}
              </NavLink>
              <NavLink
                className="child"
                to="seo-services"
                onClick={closeAllDropdowns}
              >
                {t("SEO")}
              </NavLink>
            </div>
          </div>

          <div className="flex flex-col items-center relative">
            <button
              className={`${openDropdown === "event" && "active"}`}
              onClick={() => toggleDropdown("event")}
            >
              {t("Event Management")}
            </button>
            <div
              className={`list flex overflow-hidden mt-4 duration-500 ${
                openDropdown === "event" ? "h-14" : "h-0"
              } absolute top-[100%] ${i18n.language === 'ar' ?"" :"right-0"}  z-50`}
            >
              <NavLink
                to="Implementation"
                className="child"
                onClick={closeAllDropdowns}
              >
                {t("Implementation of conference-related work")}
              </NavLink>
              <NavLink
                to="Conference"
                className="child"
                onClick={closeAllDropdowns}
              >
                {t("Conference management and organization")}
              </NavLink>
            </div>
          </div>
        </div>
      )}

      <div className="data px-[2vw] md:px-[7vw] py-5 fade-in -z-[100]">
        {filter ? <ResponsiveWorks setFilter={setFilter} /> : <Outlet />}
      </div>
    </div>
  );
};

export default PortfolioWorks;